import React, { useState, useEffect } from 'react';
import './Calculator.css';

import { Heading } from '../../../components';

import { FaRulerVertical, FaRulerHorizontal, FaLayerGroup, FaCubes, FaAngleDown } from 'react-icons/fa';



const CostCalculator = () => {

    // Define flooring costs per square foot
    const flooringCosts = {
        hardwood: 5,
        laminate: 3,
        vinyl: 2,
        tile: 4,
        carpet: 1
    };

    // Define material quality multipliers
    const materialQualityMultiplier = {
        basic: 1,
        standard: 1.5,
        premium: 2
    };

    // Define extra services costs per square foot
    const extraServicesCost = {
        installation: 1.5,
        removal: 1,
        underlayment: 0.5
    };

    // State for form inputs
    const [formData, setFormData] = useState({
        height: '',
        width: '',
        flooringType: '',
        materialQuality: ''
    });

    const [services, setServices] = useState({
        installation: false,
        removal: false,
        underlayment: false
    });

    const [totalCost, setTotalCost] = useState('0.00');

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle checkbox changes
    const handleServiceChange = (e) => {
        const { name, checked } = e.target;
        setServices(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    // Calculate cost whenever inputs change
    useEffect(() => {
        calculateTotalCost();
    }, [formData, services]);

    const calculateTotalCost = () => {
        const { height, width, flooringType, materialQuality } = formData;
        
        // Parse numeric values
        const heightNum = parseFloat(height) || 0;
        const widthNum = parseFloat(width) || 0;

        // Validate required fields
        if (!heightNum || !widthNum || !flooringType || !materialQuality) {
            setTotalCost('0.00');
            return;
        }

        // Calculate area
        const area = heightNum * widthNum;

        // Calculate base cost
        const baseCost = area * flooringCosts[flooringType] * materialQualityMultiplier[materialQuality];

        // Calculate extra services cost
        let extraCost = 0;
        if (services.installation) extraCost += area * extraServicesCost.installation;
        if (services.removal) extraCost += area * extraServicesCost.removal;
        if (services.underlayment) extraCost += area * extraServicesCost.underlayment;

        // Calculate total cost
        const total = baseCost + extraCost;

        // Update total cost
        setTotalCost(total.toFixed(2));
    };

    return (
        <section className="custom-form cost-calculator">
            <div className="box-container">
                <div className="content">
                    <form className="calculator-form dark-form">

                        {/* Form Heading */}
                        <Heading title={"Cost Calculator"} subTitle={"Calculate Your Floor Costs"} linear sideHeading />

                        <div className="input-box">
                            {/* Height Input Field */}
                            <div className="input-field">
                                <label htmlFor="height"><FaRulerVertical /></label>
                                <input
                                    type="number"
                                    name="height"
                                    className="box"
                                    id="height"
                                    min="0"
                                    placeholder="Room Length (ft)"
                                    value={formData.height}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-box">
                            {/* Width Input Field */}
                            <div className="input-field">
                                <label htmlFor="width"><FaRulerHorizontal /></label>
                                <input
                                    type="number"
                                    name="width"
                                    className="box"
                                    id="width"
                                    min="0"
                                    placeholder="Room Width (ft)"
                                    value={formData.width}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-box">
                            {/* Flooring Type Select Field */}
                            <div className="select">
                                <div className="select-container">
                                    <label htmlFor="flooringType"><FaLayerGroup /></label>
                                    <select
                                        className="select-box"
                                        id="flooringType"
                                        name="flooringType"
                                        value={formData.flooringType}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Flooring Type</option>
                                        <option value="hardwood">Hardwood</option>
                                        <option value="laminate">Laminate</option>
                                        <option value="vinyl">Vinyl</option>
                                        <option value="tile">Tile</option>
                                        <option value="carpet">Carpet</option>
                                    </select>
                                    <div className="icon-container">
                                        <FaAngleDown className="icon" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="input-box">
                            {/* Material Quality Select Field */}
                            <div className="select">
                                <div className="select-container">
                                    <label htmlFor="materialQuality"><FaCubes /></label>
                                    <select
                                        className="select-box"
                                        id="materialQuality"
                                        name="materialQuality"
                                        value={formData.materialQuality}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Material Quality</option>
                                        <option value="basic">Basic</option>
                                        <option value="standard">Standard</option>
                                        <option value="premium">Premium</option>
                                    </select>
                                    <div className="icon-container">
                                        <FaAngleDown className="icon" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Extra Services Checkboxes */}
                        <div className="extra-services">
                            {/* Installation Checkbox */}
                            <div className="checkbox-label">
                                <input
                                    type="checkbox"
                                    id="installation-checkbox"
                                    name="installation"
                                    checked={services.installation}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="installation-checkbox">Installation</label>
                                <div className="checkbox-indicator"></div>
                            </div>
                            
                            {/* Removal of Old Checkbox */}
                            <div className="checkbox-label">
                                <input
                                    type="checkbox"
                                    id="removal-checkbox"
                                    name="removal"
                                    checked={services.removal}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="removal-checkbox">Removal of Old</label>
                                <div className="checkbox-indicator"></div>
                            </div>
                            
                            {/* Underlayment Checkbox */}
                            <div className="checkbox-label">
                                <input
                                    type="checkbox"
                                    id="underlayment-checkbox"
                                    name="underlayment"
                                    checked={services.underlayment}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="underlayment-checkbox">Underlayment</label>
                                <div className="checkbox-indicator"></div>
                            </div>
                        </div>

                        {/* Total Cost Display */}
                        <div className="cost-total">
                            <h5>total</h5>
                            <span>${totalCost}</span>
                        </div>

                    </form>
                </div>
            </div>
        </section>
    );
};

export default CostCalculator;